const MAX_RETRIES = 3
const DELAY = 1000
const STATUS_REGEX = /^(408|429|5\d{2})$/
const DESCRIPTION_REGEX = /(Failed to fetch|Load failed|NetworkError)/
export const API_V2_REGEX = /api\.vicampo(-[a-z]+)?\.[^\s/]+\/v2/

const DISABLED_EXTRACTION = /\/(view|helpful\/vote)$/

class RequestHelper {
  static async sendGetRequest(url) {
    try {
      return await _fetchWithRetry(url, _getBaseConfig('GET'))
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendPostRequest(url, data) {
    const config = _getBaseConfig('POST')
    try {
      return await _fetchWithRetry(
        url,
        { ...config, body: JSON.stringify(data) },
        1
      )
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendPatchRequest(url, data) {
    const config = _getBaseConfig('PATCH')
    try {
      return await _fetchWithRetry(url, {
        ...config,
        body: JSON.stringify(data)
      })
    } catch (e) {
      _handleError(e)
    }
  }

  static async sendDeleteRequest(url) {
    try {
      return await _fetchWithRetry(url, _getBaseConfig('DELETE'))
    } catch (e) {
      _handleError(e)
    }
  }
}

const withoutHeader = /(GET|DELETE)/

const _getBaseConfig = (method) => {
  if (withoutHeader.test(method)) {
    return {
      method,
      credentials: 'include',
      headers: { Pathname: location.pathname }
    }
  }
  return {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Pathname: location.pathname
    }
  }
}

const _extractResponse = async (response) => {
  const { status, headers: originalHeaders, ok, url } = response

  // prevent error if tracking is disabled on the fly (analytics intercepts the request and fails it)
  if (DISABLED_EXTRACTION.test(url)) {
    return { headers: {}, data: {} }
  }
  if (!ok) {
    const data = await response.json()
    // Support both { error: {...} } and v2-style { data: { error: {...} } }
    const errorData = data?.error ?? data?.data?.error

    const cause = errorData?.vicampoErrorCode
    const description = errorData?.description

    const error = new Error(
      `[${url}] HTTP-${status} – description: ${description}`,
      { cause }
    )

    error.status = status
    error.cause = cause
    error.description = description
    error.errorData = errorData
    error.field = errorData?.field
    error.fields = errorData?.fields
    error.address_type = errorData?.address_type

    throw error
  }

  const headers = _getAsPlainObject(originalHeaders)

  if (status < 200 || status > 202) return { headers, data: {} }
  const data = await _extractDataField(response)

  return { headers, data }
}

const _handleError = (originalError) => {
  if (!originalError || typeof originalError !== 'object') {
    throw originalError
  }

  const {
    message,
    status,
    cause,
    description,
    stack,
    errorData,
    field,
    fields,
    address_type
  } = originalError
  let errorMessage

  try {
    errorMessage = JSON.stringify({ status, description: message })
  } catch (stringifyError) {
    console.error('Failed to stringify error details:', stringifyError)
    errorMessage = `[HTTP-${status}] Error: ${message}`
  }

  const e = new Error(errorMessage)

  e.status = status
  e.cause = cause
  e.description = description
  e.originalError = originalError
  e.errorData = errorData
  e.field = field
  e.fields = fields
  e.address_type = address_type
  if (stack) {
    e.stack = stack
  }

  throw e
}

const _extractDataField = async (response) => {
  try {
    const payload = await response.json()
    return API_V2_REGEX.test(response.url) ? payload.data : payload
  } catch (e) {
    _prefixUrl(e, response.url)
    throw e
  }
}

// Bare fetch() network failures ("Failed to fetch", "Load failed") carry no
// URL; add it so error logs are actionable (VIP-12034)
const _prefixUrl = (error, url) => {
  if (error?.urlPrefixed) return
  if (typeof error?.message !== 'string' || error.message.includes(url)) return
  try {
    error.message = `[${url}] ${error.message}`
    error.urlPrefixed = true
  } catch {
    // message is getter-only on some errors (e.g. DOMException); leave as-is
  }
}

const _fetchWithRetry = async (
  url,
  options,
  maxRetries = MAX_RETRIES,
  attempt = 1
) => {
  try {
    return await _extractResponse(await fetch(url, options))
  } catch (e) {
    const retry = attempt < maxRetries && _shouldRetry(e)

    if (!retry) {
      if (e?.status === undefined) _prefixUrl(e, url)
      throw e
    }
    // Request failed, retrying
    await new Promise((resolve) => setTimeout(resolve, DELAY))
    return await _fetchWithRetry(url, options, maxRetries, attempt + 1)
  }
}

const _shouldRetry = ({ description, status, message }) => {
  return (
    STATUS_REGEX.test(status) ||
    DESCRIPTION_REGEX.test(description) ||
    (message && DESCRIPTION_REGEX.test(message))
  )
}

const _getAsPlainObject = (headers) =>
  Array.from(headers.entries()).reduce(
    (collected, [key, value]) => ({ ...collected, [key]: value }),
    {}
  )

export default RequestHelper
